import { motion } from 'framer-motion';
import { Calendar, Users, Rocket, Target, CheckCircle, ArrowRight, Clock, TrendingUp, Shield, Zap } from 'lucide-react';

const InsurancePartnershipJourney = () => {
  const stages = [
    {
      phase: "Phase 1",
      title: "Assessment & Roadmap",
      duration: "30 days",
      icon: Target,
      color: "#90E0EF",
      description: "Comprehensive evaluation of your current processes, identification of key opportunities, and custom roadmap creation.",
      deliverables: [
        "Complete risk assessment and gap analysis",
        "Custom integration roadmap with timeline",
        "ROI projections and success metrics",
        "Stakeholder alignment and change management plan"
      ],
      results: "Clear path forward with quantified opportunities"
    },
    {
      phase: "Phase 2", 
      title: "Implementation & Integration",
      duration: "60 days",
      icon: Rocket,
      color: "#0077B6",
      description: "Seamless platform deployment with your existing systems, comprehensive team training, and initial optimization.",
      deliverables: [
        "Full platform deployment and system integration",
        "Staff training and certification programs",
        "Initial AI model calibration for your data",
        "Quality assurance testing and validation"
      ],
      results: "Live platform with trained team and initial results"
    },
    {
      phase: "Phase 3",
      title: "Optimization & Scale",
      duration: "90 days", 
      icon: TrendingUp,
      color: "#03045E",
      description: "Continuous improvement based on real data, advanced feature rollout, and performance optimization.",
      deliverables: [
        "Performance analytics and optimization recommendations",
        "Advanced feature deployment (fraud detection, predictive models)",
        "Process refinement and workflow automation",
        "Regular performance reviews and adjustments"
      ],
      results: "Measurable ROI and significant operational improvements"
    },
    {
      phase: "Phase 4",
      title: "Strategic Partnership",
      duration: "Ongoing",
      icon: Users,
      color: "#00B4D8",
      description: "Long-term partnership with continuous innovation, strategic planning, and market leadership positioning.",
      deliverables: [
        "Quarterly business reviews and strategic planning",
        "Access to latest innovations and market insights",
        "Dedicated account management and priority support",
        "Industry benchmarking and competitive analysis"
      ],
      results: "Market leadership and sustained competitive advantage"
    }
  ];

  const milestones = [
    { day: 30, achievement: "Platform Assessment Complete", value: "Roadmap Delivered" },
    { day: 60, achievement: "System Integration Live", value: "Team Trained" },
    { day: 90, achievement: "First Fraud Detection", value: "Claims Prevented" },
    { day: 120, achievement: "ROI Milestone Reached", value: "25% Cost Reduction" },
    { day: 180, achievement: "Advanced Analytics Active", value: "Predictive Insights" },
    { day: 365, achievement: "Strategic Partnership", value: "Market Leadership" }
  ];

  const benefits = [
    {
      icon: Shield,
      title: "Risk Mitigation",
      description: "Proven methodology reduces implementation risk by 85%",
      metric: "85% lower risk"
    },
    {
      icon: Clock,
      title: "Faster ROI",
      description: "Structured approach delivers measurable results within 90 days",
      metric: "90 days to ROI"
    },
    {
      icon: TrendingUp,
      title: "Scalable Growth",
      description: "Framework designed to scale with your business expansion",
      metric: "500% scalability"
    },
    {
      icon: Zap,
      title: "Rapid Deployment",
      description: "Accelerated implementation with minimal business disruption",
      metric: "50% faster launch"
    }
  ];

  return (
    <section id="partnership-journey" className="py-20 bg-gradient-to-br from-[#CAF0F8]/20 via-white to-[#90E0EF]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 bg-[#90E0EF]/20 border border-[#90E0EF]/30 rounded-full mb-6">
            <Calendar className="w-4 h-4 text-[#03045E] mr-2" />
            <span className="text-[#03045E] font-semibold text-sm">Proven 4-Stage Partnership Journey</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-[#03045E] mb-6">
            From <span className="text-[#0077B6]">Assessment to Market Leadership</span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Our structured partnership approach ensures successful transformation with predictable timelines, 
            measurable milestones, and guaranteed ROI. Join 50+ insurance leaders who've already made the journey.
          </p>
        </motion.div>

        {/* Partnership Benefits */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group">
                <div className="bg-[#90E0EF]/10 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-[#0077B6]/10 transition-colors">
                  <benefit.icon className="w-7 h-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold text-[#03045E] mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{benefit.description}</p>
                <div className="text-2xl font-bold text-[#0077B6]">{benefit.metric}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Journey Stages */}
        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl font-bold text-[#03045E] mb-4">Partnership Journey Stages</h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Each stage builds on the previous one, ensuring smooth progression and measurable results at every step.
            </p>
          </motion.div>

          <div className="space-y-8">
            {stages.map((stage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`flex ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} flex-col items-center gap-8`}
              >
                
                {/* Stage Content */}
                <div className="flex-1 bg-white rounded-3xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start space-x-4 mb-6">
                    <div 
                      className="p-4 rounded-2xl flex-shrink-0"
                      style={{ backgroundColor: `${stage.color}20` }}
                    >
                      <stage.icon 
                        className="w-8 h-8" 
                        style={{ color: stage.color }}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span 
                          className="px-3 py-1 rounded-full text-sm font-semibold"
                          style={{ 
                            backgroundColor: `${stage.color}20`,
                            color: stage.color
                          }}
                        >
                          {stage.phase}
                        </span>
                        <span className="text-sm text-gray-500 flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {stage.duration}
                        </span>
                      </div>
                      <h4 className="text-2xl font-bold text-[#03045E] mb-3">{stage.title}</h4>
                      <p className="text-gray-600 mb-6 leading-relaxed">{stage.description}</p>
                      
                      {/* Deliverables */}
                      <div className="space-y-2 mb-6">
                        <h5 className="text-sm font-semibold text-[#03045E] mb-3">Key Deliverables:</h5>
                        {stage.deliverables.map((deliverable, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-600">{deliverable}</span>
                          </div>
                        ))}
                      </div>

                      {/* Results */}
                      <div 
                        className="p-4 rounded-xl"
                        style={{ backgroundColor: `${stage.color}10` }}
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          <Target className="w-4 h-4" style={{ color: stage.color }} />
                          <span className="text-sm font-semibold text-[#03045E]">Expected Results:</span>
                        </div>
                        <p className="text-sm text-gray-700">{stage.results}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stage Visual/Number */}
                <div className="flex-shrink-0">
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                    viewport={{ once: true }}
                    className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold text-white shadow-lg"
                    style={{ backgroundColor: stage.color }}
                  >
                    {index + 1}
                  </motion.div>
                  
                  {/* Connector Line */}
                  {index < stages.length - 1 && (
                    <motion.div
                      initial={{ height: 0 }}
                      whileInView={{ height: '60px' }}
                      transition={{ duration: 0.8, delay: 0.5 + index * 0.1 }}
                      viewport={{ once: true }}
                      className="w-1 mx-auto mt-4"
                      style={{ backgroundColor: stages[index + 1].color }}
                    />
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Timeline Milestones */}
        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl font-bold text-[#03045E] mb-4">Key Milestones Timeline</h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Track your progress with clear milestones and measurable achievements throughout the partnership journey.
            </p>
          </motion.div>

          <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
            <div className="relative">
              {/* Timeline Line */}
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: '100%' }}
                transition={{ duration: 2, delay: 0.5 }}
                viewport={{ once: true }}
                className="absolute top-5 left-0 h-1 bg-gradient-to-r from-[#90E0EF] via-[#0077B6] to-[#03045E] rounded-full"
              />

              {/* Milestones */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {milestones.map((milestone, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center relative"
                  >
                    {/* Milestone Dot */}
                    <div className="w-4 h-4 bg-[#0077B6] rounded-full mx-auto mb-3 shadow-lg relative z-10"></div>
                    
                    {/* Day */}
                    <div className="text-sm font-bold text-[#03045E] mb-2">Day {milestone.day}</div>
                    
                    {/* Achievement */}
                    <div className="text-xs text-gray-600 mb-1 leading-tight">{milestone.achievement}</div>
                    
                    {/* Value */}
                    <div className="text-xs font-semibold text-[#0077B6] bg-[#90E0EF]/10 px-2 py-1 rounded-full">
                      {milestone.value}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-[#90E0EF]/30">
            <h3 className="text-2xl md:text-3xl font-bold text-[#03045E] mb-4">
              Ready to start your transformation journey?
            </h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Book a strategic consultation to discuss your specific needs and see how our partnership approach 
              can deliver results for your organization.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                onClick={() => {
                  const element = document.getElementById('packages');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#03045E] text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#03045E]/90 transition-all duration-300 shadow-lg inline-flex items-center justify-center space-x-2"
              >
                <span>View Partnership Packages</span>
                <ArrowRight className="w-5 h-5" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-transparent border-2 border-[#0077B6] text-[#0077B6] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#0077B6] hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-2"
              >
                <Calendar className="w-5 h-5" />
                <span>Schedule Consultation</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InsurancePartnershipJourney;
